/**
 * Client API Frontend - Abstraction pour consommation des API
 *
 * Usage :
 *   import { apiClient } from '@/lib/api-client';
 *
 *   const { result } = await apiClient.get<Contact[]>('/api/admin/contacts');
 *   const { result } = await apiClient.post<Contact>('/api/admin/contacts', { email, ... });
 *
 * Gestion d'erreur :
 *   try {
 *     const { result } = await apiClient.get('/api/...');
 *   } catch (err) {
 *     if (err instanceof ApiClientError) {
 *       console.error(err.response.detail); // Message d'erreur
 *       console.error(err.response.trace_id); // Pour debug
 *     }
 *   }
 */

import type { ApiResponse, ApiSuccessResponse, ApiErrorResponse } from './api-types';

export class ApiClientError extends Error {
  constructor(public response: ApiErrorResponse) {
    super(response.detail);
    this.name = 'ApiClientError';
  }
}

export class ApiClient {
  /**
   * Méthode fetch générique avec parsing automatique et gestion d'erreur
   */
  async fetch<T>(url: string, options?: RequestInit): Promise<ApiSuccessResponse<T>> {
    const response = await fetch(url, options);
    const data: ApiResponse<T> = await response.json();

    if (!data.ok) {
      throw new ApiClientError(data as ApiErrorResponse);
    }

    return data as ApiSuccessResponse<T>;
  }

  /**
   * GET request
   */
  async get<T>(url: string): Promise<ApiSuccessResponse<T>> {
    return this.fetch<T>(url, { method: 'GET' });
  }

  /**
   * POST request avec body JSON
   */
  async post<T>(url: string, body: unknown): Promise<ApiSuccessResponse<T>> {
    return this.fetch<T>(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  /**
   * PATCH request avec body JSON
   */
  async patch<T>(url: string, body: unknown): Promise<ApiSuccessResponse<T>> {
    return this.fetch<T>(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(url: string): Promise<ApiSuccessResponse<T>> {
    return this.fetch<T>(url, { method: 'DELETE' });
  }

  /**
   * PUT request avec body JSON
   */
  async put<T>(url: string, body: unknown): Promise<ApiSuccessResponse<T>> {
    return this.fetch<T>(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  /**
   * POST avec FormData (pour uploads de fichiers)
   * Note: Ne pas définir Content-Type manuellement, le navigateur le fait automatiquement avec boundary
   */
  async postFormData<T>(url: string, formData: FormData): Promise<ApiSuccessResponse<T>> {
    return this.fetch<T>(url, {
      method: 'POST',
      body: formData,
    });
  }
}

export const apiClient = new ApiClient();
